import { useState } from "react";
import { FaArrowCircleUp, FaBars, FaHandHoldingHeart, FaSyncAlt, FaUserPlus } from "react-icons/fa";
import { HiDownload } from "react-icons/hi";
import {BoxStats} from '../BoxStats'
import {BoxWrapper} from '../BoxWrapper'
import { useSelector } from 'react-redux'
import { RootState } from '../../state/store'
import {CurrencyFormat} from "../shared/Formats";
import { FilteredDashboardStats } from '../../types/dashboard.types';
import { DashboardFilters, getDonationsExport, getRegistrationsExport } from '../../services/DashboardService';
import { downloadCsv } from '../../utils/csvDownload';
import { showErrorToast } from '../../lib/ToastUtility';

type DashbardFoodieStatsProps = {
  compareStats?: FilteredDashboardStats | null;
  compareLabel?: string;
  filters: DashboardFilters;
};

export default function DashbardFoodieStats({ compareStats = null, compareLabel = 'Compare', filters }: DashbardFoodieStatsProps) {

  const { filteredStats, filteredStatsLoading, error } = useSelector((state:RootState) => state.dashboard);
  const [downloadingRegistrations, setDownloadingRegistrations] = useState(false);
  const [downloadingDonations, setDownloadingDonations] = useState(false);

  const handleDownloadRegistrations = async () => {
    setDownloadingRegistrations(true);
    try {
      const response = await getRegistrationsExport(filters);
      const rows = (response.data as { data: { name: string; email: string; registeredAt: string | null }[] })?.data ?? [];
      if (!rows.length) {
        showErrorToast('No registrations found for the selected period.');
        return;
      }
      downloadCsv(
        `registrations_${filters.fromDate}_to_${filters.toDate}`,
        rows.map((r) => ({ Name: r.name, Email: r.email, 'Registered date': r.registeredAt ?? '' })),
      );
    } catch {
      showErrorToast('Failed to download registrations.');
    } finally {
      setDownloadingRegistrations(false);
    }
  };

  const handleDownloadDonations = async () => {
    setDownloadingDonations(true);
    try {
      const response = await getDonationsExport(filters);
      const rows = (response.data as {
        data: {
          donorName: string;
          email: string;
          campaignName: string;
          donatedBars: number;
          donatedAmountUsd: number;
          currency: string;
          donationDate: string | null;
        }[];
      })?.data ?? [];
      if (!rows.length) {
        showErrorToast('No donations found for the selected period.');
        return;
      }
      downloadCsv(
        `donations_${filters.fromDate}_to_${filters.toDate}`,
        rows.map((r) => ({
          'Donor name': r.donorName,
          Email: r.email,
          Campaign: r.campaignName,
          'Donated bars': r.donatedBars,
          'Donated amount (USD)': r.donatedAmountUsd,
          Currency: r.currency,
          'Donation date': r.donationDate ?? '',
        })),
      );
    } catch {
      showErrorToast('Failed to download donations.');
    } finally {
      setDownloadingDonations(false);
    }
  };

  const DownloadButton = ({
    onClick,
    loading,
    label,
  }: {
    onClick: () => void;
    loading: boolean;
    label: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      aria-label={label}
      title={label}
      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-xl border border-white/60 bg-white/70 px-2.5 py-1.5 text-xs font-semibold text-gray-600 shadow-sm transition-colors hover:bg-white disabled:opacity-50 dark:border-white/10 dark:bg-slate-800/70 dark:text-gray-300 dark:hover:bg-slate-700"
    >
      <HiDownload className={`h-3.5 w-3.5 ${loading ? 'animate-bounce' : ''}`} />
      CSV
    </button>
  );

  return (
    <div className="flex flex-col gap-4 w-full">
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
			<BoxWrapper
			 className={"bg-gradient-to-br from-emerald-50/80 via-white/70 to-teal-100/80 dark:from-emerald-900/30 dark:via-slate-900/80 dark:to-teal-900/25 border-emerald-200/60 dark:border-emerald-400/25"}>
        <DownloadButton onClick={handleDownloadDonations} loading={downloadingDonations} label="Download donations CSV" />
        <BoxStats
              title={"Total donations (USD)"}
		      stats={filteredStats.totalDonationAmount}
		      compareStats={compareStats?.totalDonationAmount ?? null}
		      compareLabel={compareLabel}
		      formatNumber={CurrencyFormat}
              styles="bg-emerald-100/80 dark:bg-emerald-400/20"
              icon={<FaHandHoldingHeart className="text-xl text-primary-100"/>}
            />
        </BoxWrapper>

			<BoxWrapper
			 className={"bg-gradient-to-br from-lime-50/80 via-white/70 to-green-100/80 dark:from-lime-900/30 dark:via-slate-900/80 dark:to-green-900/25 border-lime-200/60 dark:border-lime-400/25"}>
        <DownloadButton onClick={handleDownloadDonations} loading={downloadingDonations} label="Download donations CSV" />
        <BoxStats
              title={"Total bars donated"}
		      stats={filteredStats.totalBars}
		      compareStats={compareStats?.totalBars ?? null}
		      compareLabel={compareLabel}
              styles="bg-lime-100/80 dark:bg-lime-400/20"
              icon={<FaBars className="text-xl text-primary-100"/>}
            />
        </BoxWrapper>
		</div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full" aria-label="Donation source breakdown">
      {([
        {
          key: 'trackYourImpactDonationAmount',
          title: 'Total TrackYourImpact donations (USD)',
        },
        {
          key: 'syncedTap2FeedDonationAmount',
          title: 'Total Tap2Feed donations synced to SASI (USD)',
        },
        {
          key: 'unsyncedTap2FeedDonationAmount',
          title: 'Tap2Feed not synced to SASI (USD)',
        },
      ] as const).map(({ key, title }) => {
        const value = filteredStats[key];
        const unavailable = Boolean(error) || typeof value !== 'number' || !Number.isFinite(value);
        return (
          <BoxWrapper key={key} className="min-w-0 bg-gradient-to-br from-amber-50/80 via-white/70 to-orange-100/80 dark:from-amber-900/30 dark:via-slate-900/80 dark:to-orange-900/25 border-amber-200/60 dark:border-amber-400/25">
            <BoxStats
              title={title}
              stats={filteredStatsLoading ? 'Loading...' : unavailable ? 'Unavailable' : value}
              compareStats={compareStats?.[key] ?? null}
              compareLabel={compareLabel}
              formatNumber={CurrencyFormat}
              metricClassName="text-3xl xl:text-4xl 2xl:text-5xl"
              styles="bg-amber-100/80 dark:bg-amber-400/20"
              icon={<FaSyncAlt className="text-xl text-primary-100" />}
            />
            {key === 'unsyncedTap2FeedDonationAmount' && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Rounding balance (USD): {filteredStatsLoading
                  ? 'Loading...'
                  : !error && typeof filteredStats.tap2FeedBalanceAmount === 'number' && Number.isFinite(filteredStats.tap2FeedBalanceAmount)
                    ? CurrencyFormat(filteredStats.tap2FeedBalanceAmount)
                    : 'Unavailable'}
              </p>
            )}
            {!filteredStatsLoading && unavailable && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Donation source totals could not be loaded. Refresh after the dashboard API is available.
              </p>
            )}
          </BoxWrapper>
        );
      })}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <BoxWrapper
			className={"bg-gradient-to-br from-cyan-50/80 via-white/70 to-sky-100/80 dark:from-cyan-900/30 dark:via-slate-900/80 dark:to-blue-900/25 border-cyan-200/60 dark:border-cyan-400/25"}>
        <DownloadButton onClick={handleDownloadRegistrations} loading={downloadingRegistrations} label="Download registrations CSV" />
        <BoxStats
              title={"Total registrations"}
              stats={filteredStats.totalRegistrations}
		      compareStats={compareStats?.totalRegistrations ?? null}
		      compareLabel={compareLabel}
              styles="bg-cyan-100/80 dark:bg-cyan-400/20"
              icon={<FaUserPlus className="text-xl text-primary-100"/>}
            />
        </BoxWrapper>
			<BoxWrapper className={"bg-gradient-to-br from-violet-50/80 via-white/70 to-indigo-100/80 dark:from-violet-900/30 dark:via-slate-900/80 dark:to-indigo-900/25 border-violet-200/60 dark:border-violet-400/25"}>
            <BoxStats
              title={"Invite FOODIE clicks"}
              stats={filteredStats.inviteFOODIEClicks}
		      compareStats={compareStats?.inviteFOODIEClicks ?? null}
		      compareLabel={compareLabel}
              styles="bg-violet-100/80 dark:bg-violet-400/20"
              icon={<FaArrowCircleUp className="text-xl text-primary-100"/>}
            />
        </BoxWrapper>
		</div>
    </div>
  )
}
